import React from 'react';
import { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import AdminRoute from './AdminRoute';
import AppLayout from '../components/AppLayout';
import { useTopBarTitle } from '../contexts/TopBarContext';

// Page imports
import LoginPage from '../pages/LoginPage';
import OverviewPage from '../pages/OverviewPage';
import DashboardListPage from '../pages/DashboardListPage';
import DashboardViewPage from '../pages/DashboardViewPage';
import ChartEditPage from '../pages/ChartEditPage';
import DataModelListPage from '../pages/DataModelListPage';
import DataModelDetailPage from '../pages/DataModelDetailPage';
import DiscoverDataPage from '../pages/DiscoverDataPage';
import BusinessViewDetailPage from '../pages/BusinessViewDetailPage';
import DataSourceListPage from '../pages/DataSourceListPage';
import ConnectionPage from '../components/connections/ConnectionPage';
import OAuthCallbackPage from '../pages/OAuthCallbackPage';
import SkeletonTestPage from '../pages/SkeletonTestPage';
import AdminUsersPage from '../pages/AdminUsersPage';
import AdminErrorReportsPage from '../pages/AdminErrorReportsPage';
import AdminSignInAccessPage from '../pages/AdminSignInAccessPage';
import NotFoundPage from '../pages/NotFoundPage';
import SharedDashboardPage from '../pages/SharedDashboardPage';
import AcceptSharedDashboardPage from '../pages/AcceptSharedDashboardPage';
import SharedDashboardListPage from '../pages/SharedDashboardListPage';

// Placeholder components for pages not yet created
const ComingSoon: React.FC<{ page: string }> = ({ page }) => {
  useTopBarTitle(page);
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>{page} - Coming Soon</h2>
      <p>This page is under construction.</p>
    </div>
  );
};

const SettingsPage = () => <ComingSoon page="Settings" />;

/**
 * Application route configuration
 * Uses React Router v6 data API pattern
 */
export const routes: RouteObject[] = [
  // Public routes
  {
    path: '/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: '/connections/oauth/callback/:connector',
    element: <OAuthCallbackPage />,
  },
  {
    path: '/shared/dashboards/:token',
    element: <ProtectedRoute><AcceptSharedDashboardPage /></ProtectedRoute>,
  },
  { path: '/embed/dashboards/:token', element: <SharedDashboardPage mode="embedded-readonly" /> },

  // Protected routes - all wrapped in AppLayout
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      // Root redirect to overview
      {
        index: true,
        element: <Navigate to="/overview" replace />,
      },

      // Overview
      {
        path: 'overview',
        element: <OverviewPage />,
      },

      // Dashboard routes
      {
        path: 'dashboards',
        children: [
          {
            index: true,
            element: <DashboardListPage />,
          },
          {
            path: ':id',
            children: [
              {
                index: true,
                element: <DashboardViewPage />,
              },
              {
                path: 'charts/new',
                element: <ChartEditPage />,
              },
              {
                path: 'charts/:chartId/edit',
                element: <ChartEditPage />,
              },
            ],
          },
        ],
      },

      {
        path: 'shared-dashboards',
        children: [
          { index: true, element: <SharedDashboardListPage /> },
          { path: ':id', element: <SharedDashboardPage mode="shared-readonly" /> },
        ],
      },

      // Data Model routes
      {
        path: 'data-models',
        children: [
          {
            index: true,
            element: <DataModelListPage />,
          },
          {
            path: ':id',
            children: [
              {
                index: true,
                element: <DiscoverDataPage />,
              },
              {
                path: 'studio',
                element: <DataModelDetailPage />,
              },
              {
                path: 'views/:viewId',
                element: <BusinessViewDetailPage />,
              },
            ],
          },
        ],
      },

      // Data Sources routes (unified: connections + connectors)
      {
        path: 'data-sources',
        children: [
          {
            index: true,
            element: <DataSourceListPage />,
          },
          {
            path: 'new/connect',
            element: <ConnectionPage />,
          },
          {
            path: ':id',
            element: <ConnectionPage />,
          },
        ],
      },

      // Settings route
      {
        path: 'settings',
        element: <SettingsPage />,
      },

      // Admin route. The UI guard conceals the page; API authorization remains authoritative.
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminUsersPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/error-reports',
        element: (
          <AdminRoute permission="error-reports">
            <AdminErrorReportsPage />
          </AdminRoute>
        ),
      },
      {
        path: 'admin/sign-in-access',
        element: (
          <AdminRoute permission="sign-in-access">
            <AdminSignInAccessPage />
          </AdminRoute>
        ),
      },

    ],
  },

  // Dev-only: skeleton preview (no auth, temporary)
  ...(import.meta.env.DEV
    ? [
        {
          path: '/_dev/skeleton',
          element: <SkeletonTestPage />,
        },
      ]
    : []),

  // 404 fallback
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;